import type { MembershipOrganization } from '../../types/membership';

/**
 * The next date a body's cards fall due, on or after today, as `yyyy-MM-dd`.
 * Null for a body with no fixed season.
 *
 * This mirrors MembershipOrganization#next_expiry_on on the backend, which fills
 * the same value in when a card arrives without one. It exists here so the form
 * can show the date before anything is saved, not to be the authority on it.
 *
 * A day the target month does not have (31 June, or 29 February outside a leap
 * year) rolls to the next year that does, rather than being clamped: the date
 * comes from the body's own calendar, so silently moving it would hide a
 * mistake.
 */
export function nextExpiryFor(
  organization: MembershipOrganization | null,
  from: Date = new Date()
): string | null {
  if (!organization) return null;
  const { expiry_day: day, expiry_month: month } = organization;
  if (day === null || month === null) return null;

  const startOfDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());

  for (let offset = 0; offset <= 4; offset += 1) {
    const year = startOfDay.getFullYear() + offset;
    const candidate: Date = new Date(year, month - 1, day);
    // JavaScript rolls 31 June into 1 July rather than refusing it; comparing
    // the parts back is how that gets caught.
    const exists = candidate.getMonth() === month - 1 && candidate.getDate() === day;

    if (exists && candidate >= startOfDay) return toIso(candidate);
  }

  return null;
}

function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
